import { createRoot } from "react-dom/client";
import App from "./App";
import "./styles/theme.css";
import "./styles/index.css";
import "./styles/taskpane.css";
import { handleDragAndDrop, handleEmailSelectionChange, itemChangeHandler, setEfileCategories } from "../emailHandler";
import { resumeCategoryQueue } from "../categoryQueue";
import type { MinimalEmailData } from "../../../common/src/types/types";

let _settings: Record<string, any> = {};
let smartSuggestionsEnabled: boolean = true;
let selectedEmailsInfo: MinimalEmailData[] = [];
let startedWithFolderSuggestion: string | null = null;
let keywordsMap: Map<string, string[]> = new Map();
let folderMap: Map<string, string> = new Map();

export function updateFolderKeywords(folderId: string, keywords: string[]): void {
    const currentKeywords = keywordsMap.get(folderId) || [];
    const nextKeywords = Array.from(new Set([...currentKeywords, ...keywords]));
    keywordsMap.set(folderId, nextKeywords);
}

export function setKeywordsMap(map: Map<string, string[]>): void {
    keywordsMap = map;
}
export function getKeywordsMap(): Map<string, string[]> {
    return keywordsMap;
}

export function setFolderMap(map: Map<string, string>): void {
    folderMap = map;
}

export function getFolderMap(): Map<string, string> {
    return folderMap;
}

export function setStartedWithFolderSuggestion(folder: string | null): void {
    startedWithFolderSuggestion = folder;
}

export async function getSelectedEmailsInfo(): Promise<MinimalEmailData[]> {
    if (selectedEmailsInfo.length === 0) {
        await handleEmailSelectionChange();
    };
    return selectedEmailsInfo;
}

export function setSelectedEmailsInfo(emails: MinimalEmailData[]): void {
    selectedEmailsInfo = emails;
}

export function setSetting(key: string, value: any): void {
    _settings.set(key, value);
    _settings.saveAsync((asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            console.error(`Failed to save setting ${key}:`, asyncResult.error);
        } else {
        }
    });
}

export function getSetting<T>(key: string, defaultValue: T): T {
    const value = _settings.get(key);
    return value !== undefined ? value : defaultValue;
}

export function refreshSettings(): void {
    if (Office.context?.roamingSettings) {
        _settings = Office.context.roamingSettings;
    }
}

function debounceEvent(func: (...args: any[]) => void, wait: number) {
    let timeout: number | undefined;
    return (...args: any[]) => {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => func(...args), wait);
    };
}

function mountApp(): void {
    const sideloadMessage = document.getElementById("sideload-msg");
    const appBody = document.getElementById("app-body");
    const rootElement = document.getElementById("root");

    if (!rootElement) {
        console.error("Unable to find #root element for rendering the React app.");
        return;
    }

    if (sideloadMessage) {
        sideloadMessage.style.display = "none";
    }

    if (appBody) {
        appBody.style.display = "flex";
    }

    const root = createRoot(rootElement);
    root.render(<App />);
}

function addEventHandlers(): void {
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, async (item: Office.Item) => {
        if (item) {
            itemChangeHandler();
        }
    })

    Office.context.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, debounceEvent(async () => await handleEmailSelectionChange(), 250), (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            console.error("Failed to add handler for SelectedItemsChanged:", asyncResult.error);
        }
        return;
    })

    Office.context.mailbox.addHandlerAsync(
        Office.EventType.DragAndDropEvent,
        (event: Office.DragAndDropEventArgs) => {
            handleDragAndDrop(event);
        },
        (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                //console.error("Failed to add event handler:", asyncResult.error.message);
                return;
            }
        }
    );
}

function checkForSuggestedFolders(): void {
    const item = Office.context.mailbox.item;
    if (smartSuggestionsEnabled && item) {
        item.getInitializationContextAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
                const context = result.value as unknown as { suggestedFolders: string[] };
                if (context.suggestedFolders.length !== 0) {
                    setTimeout(() => window.dispatchEvent(new CustomEvent('initialized-with-suggestion', { detail: context })), 200);
                }
            }
        });
    }
}

function initializeSettings(): void {
    _settings = Office.context.roamingSettings;
    smartSuggestionsEnabled = _settings.get("smartSuggestionsEnabled") ?? true;
}

Office.onReady((info) => {
    if (info.host === Office.HostType.Outlook) {
        mountApp();
        addEventHandlers();
        initializeSettings();
        checkForSuggestedFolders();
        setEfileCategories();
        resumeCategoryQueue();
    }
});

if (!(window as any).Office) {
    mountApp();
}
